import type {
  PropagatedAnnotation,
  TaskParcel,
} from "@/hooks/useTaskRelatedFrames";

/**
 * How close to the top of the frame (normalized 0-1) a flagged SAM 2 guess
 * may reach before it's taken for ceiling. Measured on prod (Oct 2026): every
 * flagged guess this close to the top was ceiling or a top-shelf mask that
 * spilled onto product; no flagged mid-shelf guess came within it, and none of
 * the guesses it hid matched a labeler's box. Re-check as data grows.
 *
 * Hiding drops nothing: lebron keeps every SAM2 Annotation with the fields
 * this rule reads (`needs_review`, `points`), so the hide rate, and whether a
 * hidden guess was a real gap, is measured from lebron's rows — not reported
 * from here.
 */
export const CEILING_TOP_EDGE = 0.05;

/**
 * A SAM 2 guess that is almost certainly ceiling, not a shelf gap. A top
 * shelf has no back panel, so a gap seeded there is mostly ceiling; SAM 2
 * tracks that plain, edgeless region onto any ceiling patch in the other
 * frames. Such masks run to the top of the frame, and SAM 2's own quality
 * check flags them (`needs_review`: the mask grew or came out ragged). A
 * flagged guess lower down is usually the right gap drawn a bit wide, so it
 * stays.
 */
export const isLikelyCeilingGuess = (a: PropagatedAnnotation): boolean =>
  a.source === "SAM2" &&
  a.needs_review === true &&
  Math.min(...a.points.map(([, y]) => y)) < CEILING_TOP_EDGE;

/**
 * The parcels without guesses not worth a labeler's time — applied once where
 * the parcels are read, so the prefill, the related-frame overlays and the
 * frame chips all see the same set.
 */
export const withoutHiddenGuesses = (parcels: TaskParcel[]): TaskParcel[] =>
  parcels.map((p) =>
    p.annotations.some(isLikelyCeilingGuess)
      ? {
          ...p,
          annotations: p.annotations.filter((a) => !isLikelyCeilingGuess(a)),
        }
      : p,
  );
